"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form"
import { FullScreenLoader } from "@/components/ui/full-screen-loader"
import { Input } from "@/components/ui/input"
import { PasswordInput } from "@/components/ui/password-input"
import { useRouter } from "@/i18n/navigation"
import { useAppDispatch } from "@/lib/store/hooks"
import { clearError, loginUser } from "@/lib/store/slices/authSlice"
import { showToast } from "@/lib/toast"
import { createLoginSchema, type LoginFormData } from "@/lib/validations/auth"

export function LoginForm() {
  const t = useTranslations("auth.login")
  const tValidation = useTranslations("auth.validation")
  const router = useRouter()
  const dispatch = useAppDispatch()

  const form = useForm<LoginFormData>({
    resolver: zodResolver(
      createLoginSchema(tValidation as (key: string) => string)
    ),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false
    }
  })

  const [isProcessing, setIsProcessing] = useState(false)
  const [isNavigating, setIsNavigating] = useState(false)

  const onSubmit = async (data: LoginFormData) => {
    if (isProcessing) {
      return
    }
    setIsProcessing(true)

    // Clear any previous errors
    dispatch(clearError())

    const loginPayload = {
      email: data.email,
      password: data.password,
      rememberMe: data.rememberMe
    }

    try {
      const result = await dispatch(loginUser(loginPayload))

      if (loginUser.fulfilled.match(result)) {
        showToast.success(
          t("loginSuccess") || "Login successful",
          t("welcomeBack") || "Welcome back!"
        )

        // Show loader while navigating
        setIsNavigating(true)

        // Navigate to dashboard on successful login
        router.push("/dashboard")
      } else if (loginUser.rejected.match(result)) {
        const rawErrorMessage = (result.payload as string) || ""

        // Map common error messages to translation keys
        const getTranslatedErrorMessage = (message: string): string => {
          const lowerMessage = message.toLowerCase()

          if (
            lowerMessage.includes("invalid email") ||
            lowerMessage.includes("invalid password") ||
            lowerMessage.includes("invalid credentials") ||
            message === "Invalid email or password"
          ) {
            return t("invalidCredentials")
          }

          if (
            lowerMessage.includes("too many") ||
            lowerMessage.includes("too many login attempts") ||
            message === "Too many login attempts. Please try again later"
          ) {
            return t("tooManyAttemptsFull")
          }

          if (
            lowerMessage.includes("access forbidden") ||
            lowerMessage.includes("forbidden") ||
            message === "Access forbidden"
          ) {
            return t("accessForbidden")
          }

          if (
            lowerMessage.includes("server error") ||
            message === "Server error. Please try again later"
          ) {
            return t("serverError")
          }

          if (
            lowerMessage.includes("network") ||
            lowerMessage.includes("connection")
          ) {
            return t("networkError")
          }

          // Return the original message if no translation found
          // This ensures the complete API error message is shown
          return message || t("invalidCredentials")
        }

        const translatedMessage = getTranslatedErrorMessage(rawErrorMessage)
        showToast.error(t("loginFailed") || "Login failed", translatedMessage)
      } else {
        showToast.error(
          t("loginFailed") || "Login failed",
          t("unknownLoginState") || "Unknown login state"
        )
      }
    } catch (error) {
      console.error("Login error:", error)
      showToast.error(
        t("loginFailed") || "Login failed",
        t("networkError") || "Network error. Please check your connection."
      )
    } finally {
      setIsProcessing(false)
    }
  }

  // Show full screen loader when navigating to dashboard
  if (isNavigating) {
    return <FullScreenLoader />
  }

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium text-foreground text-sm">
                {t("email")}
              </FormLabel>
              <FormControl>
                <Input
                  className="h-11 border-[#BFBFBF] border-[1.5px] focus:ring-0 focus:ring-offset-0"
                  disabled={isProcessing || form.formState.isSubmitting}
                  placeholder={t("emailPlaceholder")}
                  type="email"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="font-medium text-foreground text-sm">
                {t("password")}
              </FormLabel>
              <FormControl>
                <PasswordInput
                  className="h-11 border-[#BFBFBF] border-[1.5px] focus:ring-0 focus:ring-offset-0"
                  disabled={isProcessing || form.formState.isSubmitting}
                  placeholder={t("passwordPlaceholder")}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-center justify-between">
          <FormField
            control={form.control}
            name="rememberMe"
            render={({ field }) => (
              <FormItem className="flex items-center gap-2 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    id="rememberMe"
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel
                  className="font-medium text-foreground text-sm leading-none"
                  htmlFor="rememberMe"
                >
                  {t("rememberMe")}
                </FormLabel>
              </FormItem>
            )}
          />
          <Button
            className="px-0 font-medium text-primary"
            disabled={isProcessing || form.formState.isSubmitting}
            type="button"
            variant="link"
          >
            {t("forgotPassword")}
          </Button>
        </div>

        <Button
          aria-busy={isProcessing || form.formState.isSubmitting}
          className="h-11 w-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
          disabled={isProcessing || form.formState.isSubmitting}
          type="submit"
          variant="default"
        >
          {t("signIn")}
        </Button>
      </form>
    </Form>
  )
}
